<template>
    <card title="Store Information">
        <template #header-right>
            <history-modal v-model="store" />
        </template>
        <div class="mb-2 search-field glass-pane">
            <b-form-row>
                <b-col>
                    <SearchComponent v-model="store" :id="store && +store.id"
                        :disabled="!!(store && store.id) || newMode" search-u-r-l="/Store" :prepopulate="true"
                        @input="onStoreUpdate" label="Store" />
                </b-col>
            </b-form-row>
        </div>

        <!-- Simplified view for POR-only users -->
        <div v-if="hasPorOnlyPermission && !!store.id" class="por-only-view">
            <b-form-row class="mb-3">
                <b-col>
                    <fg-input :value="store.name" label="Store Name" disabled />
                </b-col>
            </b-form-row>
            <b-form-row class="mb-3">
                <b-col>
                    <label>
                        Pharmacist of Record
                        <help-popup placement="top">
                            Licensed pharmacist legally responsible for this pharmacy location.
                        </help-popup>
                    </label>
                    <fg-input :value="pharmacistOfRecordPretty" disabled />
                </b-col>
            </b-form-row>
            <div class="d-flex" style="gap: 10px;">
                <b-button @click="makeMyselfPharmacistOfRecord">
                    Make Myself Pharmacist of Record
                </b-button>
                <b-button variant="secondary" @click="clearStoreSelection">
                    Clear
                </b-button>
            </div>
        </div>

        <!-- Full store form for users with full store permission -->
        <b-form-group v-if="!hasPorOnlyPermission" :disabled="disableFields">
            <b-form-row>
                <b-col>
                    <fg-input ref="storeNameInput" v-model="store.name" label="Store Name" required />
                </b-col>
                <b-col cols="5">
                    <fg-input v-model="store.phoneNumber" label="Phone" />
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col>
                    <AddressFormFields v-model="store" :country="store.addressCountry">
                        <template #row-1>
                            <b-col cols="5">
                                <fg-input v-model="store.faxNumber" label="Fax" />
                            </b-col>
                        </template>
                        <template #row-2>
                            <b-col>
                                <fg-input v-model="store.emailAddress" label="Email" />
                            </b-col>
                        </template>
                    </AddressFormFields>
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col cols="3">
                    <label>Country</label>
                    <fg-select v-model="store.addressCountry" :options="countryOptions" :disabled="disableFields" />
                </b-col>
                <b-col>
                    <label>
                        Time Zone
                        <help-popup placement="top">
                            Store's time zone. Critical for prescription processing, refill scheduling, and patient notifications.
                        </help-popup>
                    </label>
                    <fg-select v-model="store.timeZone" :options="timeZoneOptions"
                        :reduce="option => option.value" :get-option-label="option => option.text"
                        :disabled="disableFields" clearable />
                </b-col>
                <b-col>
                    <fg-input v-model="store.webAddress" label="Web" />
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col>
                    <label>
                        DEA Number
                        <help-popup placement="top">
                            Drug Enforcement Administration registration number. Required for dispensing controlled substances. Used when autonmatically reporting dispenses.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.deaNumber" />
                </b-col>
                <b-col>
                    <label>
                        NPI
                        <help-popup placement="top">
                            National Provider Identifier. Required for insurance billing, claims processing and escript matching to the store record.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.npiNumber" />
                </b-col>
                <b-col>
                    <label>
                        Mail NPI
                        <help-popup placement="top">
                            Separate NPI used specifically for mail order pharmacy operations. May differ from retail NPI.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.mailNPINumber" />
                </b-col>
                <b-col>
                    <label>
                        PID
                        <help-popup placement="top">
                            Processor Identification Number. Used by pharmacy benefit managers for claim routing. Not required.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.pidNumber" />
                </b-col>
                <b-col>
                    <label>
                        NCPDP
                        <help-popup placement="top">
                            National Council for Prescription Drug Programs identifier. Unique 7-digit number for pharmacy billing.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.ncpdp" />
                </b-col>
            </b-form-row>

            <hr>
            <b-form-row>
                <b-col>
                    <label>
                        Fed ID
                        <help-popup placement="top">
                            Federal Tax ID (EIN). Used for business tax reporting and insurance contracts. Not required.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.fedIDNumber" />
                </b-col>
                <b-col>
                    <label>
                        Store Number
                        <help-popup placement="top">
                            Internal store identifier used for reporting and tracking. Not required.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.storeNumber" />
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col>
                    <label>
                        Store ID
                        <help-popup placement="top">
                            Internal system identifier for this store location. Not required.
                        </help-popup>
                    </label>
                    <fg-input v-model="store.storeID" />
                </b-col>
                <b-col>
                    <label for="launch-date">
                        Launch Date
                        <help-popup placement="top">
                            Date when this store location went live in the system. Affects what data shows in various queues after a data migration. Not required.
                        </help-popup>
                    </label>
                    <el-date-picker id="launch-date" v-model="store.launchDate" type="date" />
                </b-col>
            </b-form-row>
            <b-form-row>
                <b-col>
                    <label>Assigned Teams</label>
                    <help-popup placement="top">
                        If no teams are configured, the store is not team constrained.
                        <br />
                        As long as at least 1 team is configured prescriptions will not be processable by anyone,
                        unless they're a member of one of the store's assigned teams.
                    </help-popup>
                    <multiselect v-model="store.teams" track-by="id" :disabled="disableFields" :options="teamOptions"
                        :close-on-select="false" multiple />
                </b-col>
                <b-col>
                    <label>CancelRx Task Team</label>
                    <help-popup placement="top">
                        When a CancelRx is received, a task will be created and assigned to this team.
                        Leave empty to disable CancelRx task creation.
                    </help-popup>
                    <multiselect v-model="store.cancelRxTaskTeam" track-by="id" :disabled="disableFields"
                        :options="teamOptions" :close-on-select="true" :multiple="false" />
                </b-col>
                <b-col>
                    <label>RxTransfer Error Task Team</label>
                    <help-popup placement="top">
                        When SureScripts returns an async error on an RxTransfer (inbound or outbound),
                        a task will be created and assigned to this team.
                        Leave empty to disable RxTransfer error task creation.
                    </help-popup>
                    <multiselect v-model="store.rxTransferErrorTaskTeam" track-by="id" :disabled="disableFields"
                        :options="teamOptions" :close-on-select="true" :multiple="false" />
                </b-col>
            </b-form-row>
            <b-form-row class="mt-3">
                <b-col cols="6">
                    <label>
                        Dispenser Type
                        <help-popup placement="top">
                            Pharmacy/dispenser classification reported to state PMP/PDMP systems (ASAP 5.0 PHA14).
                        </help-popup>
                    </label>
                    <fg-select v-model="store.dispenserType" :options="dispenserTypeOptions"
                        :reduce="option => option.value" :get-option-label="option => option.text"
                        :disabled="disableFields" clearable />
                </b-col>
            </b-form-row>

            <hr>

            <b-row>
                <b-col>
                    <div>
                        <b-form-checkbox v-model="store.active">Active</b-form-checkbox>
                    </div>
                    <div>
                        <b-form-checkbox v-model="store.disableReadyForPickupTexts">
                            Disable Ready for Pickup Texts
                            <help-popup placement="top">
                                When enabled, prevents automatic text notifications when prescriptions are ready for pickup. This texting functionality needs to be enabled via the Admin -> Configuration page before this disable option takes effect.
                            </help-popup>
                        </b-form-checkbox>
                    </div>
                    <div>
                        <b-form-checkbox v-model="store.disallowTransferOut">
                            Disallow Transfer Out
                            <help-popup placement="top">
                                When enabled, prevents prescriptions from being transferred out to other pharmacies.
                            </help-popup>
                        </b-form-checkbox>
                    </div>
                    <div>
                        <b-form-checkbox v-model="store.insuranceAllowed">
                            Insurance Allowed
                            <help-popup placement="top">
                                When unchecked, insurance billing is not permitted for this store. Requires the tenant-level "Enforce Insurance Allowed" configuration to be enabled.
                            </help-popup>
                        </b-form-checkbox>
                    </div>
                    <div>
                        <b-form-checkbox v-model="store.isMailOrder">
                            Mail Order Pharmacy
                            <help-popup placement="top">
                                Identifies this store as a mail-order pharmacy. Reported to state PMP/PDMP systems (ASAP 5.0 PHA15).
                            </help-popup>
                        </b-form-checkbox>
                    </div>
                </b-col>
                <b-col>
                    <b-form-group label="Billing Flags">
                        <b-col>
                            <div>
                                <b-form-checkbox v-model="store.selectedCapabilities" value="TwoStepShipping">
                                    Two Step Shipping
                                    <help-popup placement="top">
                                        Enables two-step shipping process for better package tracking and handling. This enables the shipping page. If not enabled, shipping labels are printed at PV2.
                                    </help-popup>
                                </b-form-checkbox>
                            </div>
                            <div>
                                <b-form-checkbox v-model="store.selectedCapabilities" value="UseUSPSInState">
                                    Use USPS In State
                                    <help-popup placement="top">
                                        Use USPS as the carrier for in-state shipments instead of other carriers.
                                    </help-popup>
                                </b-form-checkbox>
                            </div>
                            <div>
                                <b-form-checkbox v-model="store.selectedCapabilities" value="HubMode">
                                    Hub Mode
                                    <help-popup placement="top">
                                        Enables hub (non-dispensing) pharmacy mode. Allows searching and selecting inactive drugs during prescription entry with restricted status options (On Hold, Void, or Transfer Out).
                                    </help-popup>
                                </b-form-checkbox>
                            </div>
                            <div>
                                <b-form-checkbox v-model="store.selectedCapabilities" value="RequireAddressApproval">
                                    Require Address Approval
                                    <help-popup placement="top">
                                        When enabled, pharmacists must review and approve USPS address corrections before a shipping label is created.
                                    </help-popup>
                                </b-form-checkbox>
                            </div>
                        </b-col>
                    </b-form-group>
                </b-col>
                <b-col v-if="!!store.id">
                    <label>
                        Pharmacist of Record
                        <help-popup placement="top">
                            Licensed pharmacist legally responsible for this pharmacy location. Required for dispensing functionality to work.
                        </help-popup>
                    </label>
                    <fg-input :value="pharmacistOfRecordPretty" disabled />

                    <b-button v-if="hasFullStorePermission" @click="makeMyselfPharmacistOfRecord">
                        Make Myself Pharmacist of Record
                    </b-button>
                </b-col>
            </b-row>

            <hr>

            <b-tabs pills lazy content-class="mt-3">
                <b-tab title="Branding">
                    <b-form-row>
                        <b-col>
                            <label>
                                Branding Name
                                <help-popup placement="top">
                                    Pharmacy name displayed to patients/prescribers on labels, documents, and communications.
                                </help-popup>
                            </label>
                            <fg-input v-model="store.brandingName" />
                        </b-col>
                        <b-col>
                            <label>
                                Branding Phone
                                <help-popup placement="top">
                                    Phone number displayed to patients/prescribers on labels, documents, and communications.
                                </help-popup>
                            </label>
                            <fg-input v-model="store.brandingPhone" />
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <label>
                                Branding Image
                                <help-popup placement="top">
                                    Logo or image displayed on patient-facing materials such as prescription labels, packaging, and documentation.
                                </help-popup>
                            </label><br>
                            <img v-if="brandingImageSrc" :src="brandingImageSrc" class="branding-image" />
                            <b-button variant="info" v-b-modal:branding-modal>Upload Branding Image...</b-button>
                            <branding-modal id="branding-modal" v-model="store" @image-uploaded="onBrandingImageUploaded" />
                        </b-col>
                    </b-form-row>
                </b-tab>
                <b-tab title="Text Messages">
                    <TextMessageTemplatesField v-model="store.textMessageTemplates" :disabled="disableFields"
                        :hide-templates="['passwordReset', 'awaitingInsurance', 'welcome', 'rxRequestDenied']"
                        :preview-data="{
                            PharmacyName: this.store.brandingName,
                            PharmacyPhone: this.store.brandingPhone,
                            PharmacyFax: this.store.faxNumber,
                            PharmacyNPI: this.store.npiNumber,
                            PharmacyNCPDP: this.store.ncpdp
                        }" />
                </b-tab>
                <b-tab title="State Options">
                    <div class="state-options-grid">
                        <StateOptionItem v-for="s in stateValues" :key="s.abbreviation" :state="s"
                            :value="stateOptionsByState[s.abbreviation]"
                            :stores="secondaryStoreOptions"
                            @change="handleStateOption(s.abbreviation, $event)" />
                    </div>
                </b-tab>
                <b-tab title="Auto Refill">
                    <b-row>
                        <b-col v-for="(value, key) in autoRefillCadenceFieldsNextProcessDate" :key="key">
                            <ConfigurationField v-model="store[key]" :label="value.title" :type="value.type"
                                :help="value.help" :options="value.options" :append="value.append" />
                        </b-col>
                    </b-row>
                </b-tab>
                <b-tab title="Inventory">
                    <b-row>
                        <b-col>
                            <label for="inventory-par-level-recipients">
                                Inventory PAR Level Alert Recipients
                                <help-popup placement="top">
                                    Users who receive alerts when inventory falls below PAR (Periodic Automatic Replenishment) levels.
                                </help-popup>
                            </label>
                            <multiselect :disabled="disableFields" id="inventory-par-level-recipients"
                                v-model="store.parLevelAlertRecipients" :options="users.map(u => u.userName)"
                                :multiple="true" :close-on-select="false" :clear-on-select="false" />
                        </b-col>
                        <b-col v-if="hasInventoryLocations">

                            <b-col>
                                <label>
                                    Default Inventory Filling Location
                                    <help-popup placement="top">
                                        Primary location used for filling prescriptions when multiple inventory locations exist.
                                    </help-popup>
                                </label>
                                <SearchComponent :id="store.defaultFillingLocation?.id"
                                    v-model="store.defaultFillingLocation" :disabled="disableFields"
                                    search-u-r-l="/inventory/inventory-locations" :prepopulate="true"
                                    clearable />
                            </b-col>

                        </b-col>
                    </b-row>
                </b-tab>
                <b-tab title="Printing">
                    <b-form-row :class="{ 'alt-mode': store.useAlternatePrinterEndpoint }">
                        <b-col class="printer">
                            <label>
                                Printer
                                <help-popup placement="top">
                                    Default printer for prescription labels and documents at this location.
                                </help-popup>
                            </label>
                            <fg-input v-model="store.defaultPrinter" />
                            <label>
                                ZPL Queue (optional)
                                <help-popup placement="top">
                                    Zebra Programming Language printer queue for label printing (optional).
                                </help-popup>
                            </label>
                            <fg-input v-model="store.zplPrinterQueue" />
                        </b-col>
                        <b-col class="alt-printer">
                            <label>
                                Alternate Printer Endpoint
                                <help-popup placement="top">
                                    HTTP endpoint URL that receives print data (labels, documents, patient education) via API instead of using local printer queues. When enabled, print jobs are sent as JSON to this external service.
                                </help-popup>
                            </label>
                            <fg-input v-model="store.alternatePrinterEndpoint" />
                            <b-form-checkbox v-model="store.useAlternatePrinterEndpoint">Use alternate printer
                                endpoint
                            </b-form-checkbox>
                        </b-col>
                    </b-form-row>
                    <b-form-row>
                        <b-col>
                            <label>
                                Parata Queue
                                <help-popup placement="top">
                                    An AWS SQS queue name for sending dispensing instructions to Parata equipment. Messages are sent to this queue and processed, communicating with Parata hardware via TCP.
                                </help-popup>
                            </label>
                            <fg-input v-model="store.parataQueue" />
                        </b-col>
                    </b-form-row>
                </b-tab>
                <b-tab title="Business Hours">
                    <label>
                        <strong>Business Hours Configuration</strong>
                        <help-popup placement="top">
                            Configure the store's operating days and hours. This determines the "next business day" when adjusting express shipment dates. If closed on weekends, express shipments closed on Friday will automatically move to Monday.
                        </help-popup>
                    </label>
                    <div v-if="!store.businessHours" class="mb-3">
                        <b-alert show variant="light" class="business-hours-alert">
                            No business hours configured. Using default: Monday-Friday, 9am-5pm (closed weekends).
                            <b-button size="sm" variant="primary" @click="initializeBusinessHours">
                                Configure Business Hours
                            </b-button>
                        </b-alert>
                    </div>
                    <b-row v-if="store.businessHours">
                        <b-col>
                            <div class="business-hours-grid">
                                <div v-for="day in daysOfWeek" :key="day.key" class="day-config">
                                    <b-form-checkbox v-model="store.businessHours[day.key].isClosed"
                                        @change="onDayClosedToggle(day.key)">
                                        <strong>{{ day.label }}</strong> - Closed
                                    </b-form-checkbox>
                                    <div v-if="!store.businessHours[day.key].isClosed" class="time-inputs">
                                        <label class="time-label">Open:</label>
                                        <el-time-picker
                                            v-model="dayTimeValues[day.key].open"
                                            format="hh:mm A"
                                            value-format="HH:mm"
                                            @change="onTimeChange(day.key, 'open', $event)"
                                            placeholder="Open Time"
                                            size="small" />
                                        <label class="time-label">Close:</label>
                                        <el-time-picker
                                            v-model="dayTimeValues[day.key].close"
                                            format="hh:mm A"
                                            value-format="HH:mm"
                                            @change="onTimeChange(day.key, 'close', $event)"
                                            placeholder="Close Time"
                                            size="small" />
                                    </div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </b-tab>
                <b-tab title="Fax Templates" v-if="!!store.id">
                    <FaxTemplateField v-model="store.faxTemplateOverrides" />
                </b-tab>
            </b-tabs>
        </b-form-group>

        <NewClearSaveButtons v-if="!hasPorOnlyPermission" :titles="buttonTitles" :disable-new="false"
            :disable-save="disableFields || !validForm" :disable-clear="disableClear" @new-clicked="createStore"
            @clear-clicked="showConfirmationModal" @save-clicked="submitInformation" sticky />
    </card>
</template>

<script lang="ts">
import { Component, Mixins, Watch } from 'vue-property-decorator';
import axios, { AxiosError } from 'axios';

import AddressFormFields from '@/components/AddressFormFields.vue';
import NewClearSaveButtons, { NewClearSaveTitles } from '@/components/NewClearSaveButtons.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import { Lock, LockHandler } from '@/mixins/LockHandler';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Store, StoreStateOption, StoreTimeZone } from '@/models/Store';
import BrandingModal from "@/components/Store/BrandingModal.vue";
import TextMessageTemplatesField from "@/components/Configuration/TextMessageTemplatesField.vue";
import ConfigurationField from "@/components/Configuration/ConfigurationField.vue";
import { autoRefillCadenceFields } from "@/models/Configuration";
import StateOptionItem from "@/components/Store/StateOptionItem.vue";
import FaxTemplateField from "@/components/Configuration/FaxTemplateField.vue";

import Multiselect from 'vue-multiselect';
import { ValidValues } from '@/data/ValidValues';
import { User } from '@/models/User';
import { Permission } from '@/models/Permission';
import { DatePicker, TimePicker } from 'element-ui';
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import { Team } from "@/models/Team";
import HelpPopup from "@/components/HelpPopup.vue";
import { EntityType } from '@/models/EntityType';

@Component({
    name: "StoreDetailsPage",
    components: {
        HelpPopup,
        HistoryModal,
        BrandingModal,
        AddressFormFields,
        NewClearSaveButtons,
        SearchComponent,
        Multiselect,
        TextMessageTemplatesField,
        [DatePicker.name]: DatePicker,
        [TimePicker.name]: TimePicker,
        ConfigurationField,
        StateOptionItem,
        FaxTemplateField,
    },
})
export default class StoreDetailsPage extends Mixins(LockHandler, DisableAutocompleteMixin) {
    private store: Store = new Store();
    private storeLock: Lock | null = null;
    private newMode = false;
    protected capabilitiesOptions = Store.flagOptions;
    private brandingImageSrc: string | null = "";
    protected teamOptions: Team[] = [];
    protected showStateOptions = false;
    protected activeStores: Array<{ id: number; name: string }> = [];

    protected stateValues = ValidValues.statesData;

    get timeZoneOptions() {
        const displayNames: Record<number, string> = {
            [StoreTimeZone.PacificStandardTime]: 'Pacific Time (PT)',
            [StoreTimeZone.MountainStandardTime]: 'Mountain Time (MT)',
            [StoreTimeZone.USMountainStandardTime]: 'Mountain Time - no DST (Arizona)',
            [StoreTimeZone.CentralStandardTime]: 'Central Time (CT)',
            [StoreTimeZone.EasternStandardTime]: 'Eastern Time (ET)',
            [StoreTimeZone.USEasternStandardTime]: 'Eastern Time - no DST',
        };
        return Object.keys(StoreTimeZone)
            .filter(key => !isNaN(Number(key)))
            .map(key => ({
                text: displayNames[Number(key)] || StoreTimeZone[Number(key)],
                value: Number(key)
            }));
    }

    get countryOptions() {
        return ValidValues.countries;
    }

    get dispenserTypeOptions() {
        return [
            { value: 1, text: '01 - Independent Pharmacy' },
            { value: 2, text: '02 - Chain Pharmacy' },
            { value: 3, text: '03 - Long-term Care Pharmacy' },
            { value: 4, text: '04 - Hospital Pharmacy' },
            { value: 5, text: '05 - Opioid Treatment Program' },
            { value: 6, text: '06 - Cannabis Dispensary' },
            { value: 7, text: '07 - Veterinary/Vet Patient Only Dispenser' },
            { value: 8, text: '08 - Dispensing Prescriber' },
            { value: 9, text: '09 - Specialty Pharmacy' },
            { value: 10, text: '10 - Federal' },
            { value: 11, text: '11 - Tribal' },
            { value: 99, text: '99 - Other' },
        ];
    }

    get autoRefillCadenceFieldsNextProcessDate() {
        return autoRefillCadenceFields(this.store.autoRefillNextProcessDateDefaultCadenceType ?? 0);
    }

    get routeId(): number {
        let res = parseInt(this.$route.params.id);
        if (isNaN(res)) return 0;
        return res;
    }

    get hasFullStorePermission(): boolean {
        return this.$permissions.some((p: Permission) =>
            p.permissionCodeName === 'Maintenance/Store');
    }

    get hasPorOnlyPermission(): boolean {
        return !this.hasFullStorePermission &&
            this.$permissions.some((p: Permission) =>
                p.permissionCodeName === 'Maintenance/Pharmacist of Record');
    }

    get disableFields(): boolean {
        if (this.newMode) return false;
        if (this.hasPorOnlyPermission) return true;

        const haveLock = !!this.storeLock;
        const storeGood = !!(this.store && this.store.id);
        return !haveLock || !storeGood;
    }

    get disableClear(): boolean {

        if (!this.storeLock || !this.storeLock.lockedBy || this.storeLock.lockedBy !== this.$user?.userName) {
            console.log("not locked by me - allowing clear");
            return false;
        }
        return this.disableFields;
    }

    protected buttonTitles: NewClearSaveTitles = {
        new: "New Store",
        clear: "Clear",
        save: "Save Changes",
        cancel: "",
    };

    created() {
        if (this.routeId) {
            this.newMode = false;
            this.fetchStoreById(this.routeId);
        }
        this.loadUsers();
        this.loadHasInventoryLocations();
        this.loadTeamOptions();
        this.loadActiveStores();
    }

    onStoreUpdate(store: Store) {
        if (store && store.id && store.id !== this.routeId) {
            this.$router.push({
                name: 'Store',
                params: { id: store.id.toString() },
            });
        }
    }

    async showConfirmationModal() {
        try {
            const value = await this.$bvModal.msgBoxConfirm('Are you sure you want to clear the store?', {
                title: 'Clear Store',
                okVariant: 'danger',
                okTitle: 'Clear',
                centered: true,
            });

            if (!value) return;
            this.clearForm();
        } catch (err) {
            console.log("Error caught in showConfirmationModal()");
            console.error(err);
        }
    }

    clearForm() {
        if (this.storeLock && this.storeLock.refreshURL) {
            this.releaseLockAtURL(this.storeLock.refreshURL);
            this.storeLock = null;
        }
        this.store = new Store();
        this.brandingImageSrc = null;
        this.newMode = false;
    }

    clearStoreSelection() {
        this.store = new Store();
        this.brandingImageSrc = null;
        if (this.routeId) {
            this.$router.push({ name: 'Store-New' });
        }
    }

    fetchStoreById(id: number) {
        const store = new Store();
        store.id = id;
        this.fetchStore(store);
    }

    async fetchStore(value: Store) {
        if (this.store && this.store.id === value.id &&
            this.storeLock && this.storeLock.refreshURL === `/Store/${value.id}/lock`) {
            return;
        }

        // Skip lock acquisition for POR-only users (they can only edit the Pharmacist of Record)
        if (!this.hasPorOnlyPermission && this.storeLock?.refreshURL !== `/Store/${value.id}/lock`) {
            try {
                const result = await this.addLockWithURL(`/Store/${value.id}/lock`, 60000);
                this.storeLock = result;
            } catch (error) {
                const err = error as AxiosError;
                if (err.response?.status == 418) {
                    // already locked
                    this.showLockedMessage(
                        EntityType.Store,
                        value.id.toString(),
                        value.name,
                        err.response.data
                    );
                } else {
                    this.$notification(NotificationOptions.error(error));
                }
            }
        }

        try {
            const response = await axios.get(`/Store/${value.id}`, { headers: { 'Cache-Control': 'no-cache' } });
            this.store = new Store(response.data);

            const hasStateOptions = !!this.store.stateOptions.length;
            const hasAllowedStates = !!this.store.allowedStates.length;

            if (!hasStateOptions && hasAllowedStates) {
                this.store.stateOptions = this.store.allowedStates.map(s => ({ state: s, ship: true, pdmp: false }));
            }
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        }

        try {
            this.brandingImageSrc = await this.loadBrandingImage();
        } catch {
            this.brandingImageSrc = null;
        }
    }

    createStore() {
        this.newMode = true;
        this.store = new Store();
        this.$nextTick(() => {
            const inputComponent = this.$refs.storeNameInput as any;
            if (inputComponent && inputComponent.$el) {
                const inputElement = inputComponent.$el.querySelector('input');
                if (inputElement) {
                    inputElement.focus();
                }
            }
        });
    }

    @Watch('$route.params.id')
    onParamChange(value: string, oldValue: string) {
        if (value != oldValue) {
            this.fetchStoreById(Number(value));
        }
    }

    @Watch('store.addressCountry')
    onCountryChange(newVal: string, oldVal: string) {
        if (oldVal && newVal !== oldVal) {
            this.store.addressState = '';
        }
    }

    @Watch('store')
    onStoreChanged(value: Store | null, _oldValue: Store | null) {
        if (!value) return;
        if (value.id && value.id != -1 && value.id != this.routeId) {
            this.$router.push({
                name: 'Store',
                params: { id: value.id.toString() },
            });
        }
    }

    async submitInformation() {
        try {
            const response = await axios.post('/Store/', this.store);
            this.store = new Store(response.data);
            this.$notification(NotificationOptions.successSaveNotificationPreset("Store"));
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.errorSaveNotificationPreset("Store", error as AxiosError));
        }
    }

    get validForm(): boolean {
        return !!(this.store?.name);
    }

    async makeMyselfPharmacistOfRecord() {
        try {
            const response = await axios.post(`/Store/${this.store.id}/MakeSelfPharmacistOfRecord`);
            this.store = new Store(response.data);
            this.$notification(NotificationOptions.notificationOptionsPreset("You have been made the Pharmacist of Record", NotificationOptions.NotificationTypes.success));
        } catch (error) {
            console.warn(error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    async loadBrandingImage(): Promise<string> {
        if (!this.store?.brandingImageId) return Promise.reject("no branding image id");

        try {
            const blob = await axios.get(`/api/Image/${this.store.brandingImageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(blob.data);
            return objectUrl;
        } catch (err) {
            return Promise.reject("failed to load branding image");
        }
    }

    private users: User[] = [];

    async loadUsers() {
        try {
            const resp = await axios.get<User[]>("/user");
            this.users = resp.data;
        } catch {
            // ignore
        }
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await axios.get<boolean>("/inventory/has-inventory-locations")).data;
        } catch {
            //ignore
        }
    }

    async loadTeamOptions() {
        try {
            const resp = await axios.get<Team[]>("/api/User/teams");
            this.teamOptions = resp.data.map(t => new Team(t));
        } catch {
            // ignore
        }
    }

    async loadActiveStores() {
        try {
            const resp = await axios.get<Store[]>("/Store", { params: { active: true } });
            this.activeStores = resp.data.map(s => ({ id: s.id, name: s.name }));
        } catch {
            // ignore
        }
    }

    get secondaryStoreOptions(): Array<{ id: number; name: string }> {
        return this.activeStores.filter(s => s.id !== this.store?.id);
    }

    get pharmacistOfRecordPretty() {
        if (!this.store?.pharmacistOfRecord) return "";
        const porUser = Object.assign(new User(), this.store.pharmacistOfRecord);
        return `${porUser.toString()} (${porUser.initials})`;
    }

    handleStateOption(state: string, value: StoreStateOption) {
        //Allowed States field
        this.store.allowedStates = this.store.allowedStates.filter(i => i !== state);
        if (value.ship) {
            this.store.allowedStates.push(state);
        }

        this.store.stateOptions = this.store.stateOptions.filter(so => so.state !== state);
        this.store.stateOptions.push(value);
    }

    get stateOptionsByState() {
        return Object.fromEntries((this.store?.stateOptions ?? []).map(so => [so.state, so]));
    }

    async onBrandingImageUploaded() {
        try {
            this.brandingImageSrc = await this.loadBrandingImage();
        } catch {
            this.brandingImageSrc = null;
        }
    }

    // Business Hours Management
    protected daysOfWeek = [
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' },
        { key: 'sunday', label: 'Sunday' }
    ];

    protected dayTimeValues: Record<string, { open: Date | null, close: Date | null }> = {
        monday: { open: null, close: null },
        tuesday: { open: null, close: null },
        wednesday: { open: null, close: null },
        thursday: { open: null, close: null },
        friday: { open: null, close: null },
        saturday: { open: null, close: null },
        sunday: { open: null, close: null }
    };

    initializeBusinessHours() {
        this.store.businessHours = {
            monday: { open: '09:00', close: '17:00', isClosed: false },
            tuesday: { open: '09:00', close: '17:00', isClosed: false },
            wednesday: { open: '09:00', close: '17:00', isClosed: false },
            thursday: { open: '09:00', close: '17:00', isClosed: false },
            friday: { open: '09:00', close: '17:00', isClosed: false },
            saturday: { open: '09:00', close: '17:00', isClosed: true },
            sunday: { open: '09:00', close: '17:00', isClosed: true }
        };
        this.syncTimePickersFromBusinessHours();
    }

    onDayClosedToggle(dayKey: string) {
        if (!this.store.businessHours || !this.store.businessHours[dayKey]) return;

        if (this.store.businessHours[dayKey].isClosed) {
            // If now closed, clear the time values
            this.store.businessHours[dayKey].open = undefined;
            this.store.businessHours[dayKey].close = undefined;
        } else {
            // If now open, set default times
            this.store.businessHours[dayKey].open = '09:00';
            this.store.businessHours[dayKey].close = '17:00';
            this.syncTimePickersFromBusinessHours();
        }
    }

    onTimeChange(dayKey: string, timeType: 'open' | 'close', value: string) {
        if (!this.store.businessHours || !this.store.businessHours[dayKey]) return;
        this.store.businessHours[dayKey][timeType] = value;
    }

    syncTimePickersFromBusinessHours() {
        if (!this.store.businessHours) return;

        this.daysOfWeek.forEach(day => {
            const dayHours = this.store.businessHours![day.key];
            if (dayHours && !dayHours.isClosed) {
                if (dayHours.open) {
                    const [hours, minutes] = dayHours.open.split(':');
                    this.dayTimeValues[day.key].open = new Date(2000, 0, 1, parseInt(hours, 10), parseInt(minutes, 10));
                }
                if (dayHours.close) {
                    const [hours, minutes] = dayHours.close.split(':');
                    this.dayTimeValues[day.key].close = new Date(2000, 0, 1, parseInt(hours, 10), parseInt(minutes, 10));
                }
            } else {
                this.dayTimeValues[day.key].open = null;
                this.dayTimeValues[day.key].close = null;
            }
        });
    }

    @Watch('store.businessHours', { deep: true, immediate: true })
    onBusinessHoursChange() {
        this.syncTimePickersFromBusinessHours();
    }
}

</script>

<style scoped>
.form-row {

    .alt-printer,
    .printer {
        transition: all .3s;
    }

    .alt-printer .form-group {
        opacity: .5;
    }
}

.form-row.alt-mode {
    .printer {
        opacity: .5;
    }

    .alt-printer .form-group {
        opacity: 1;
    }
}

.branding-image {
    max-width: 250px;
    display: block;
}

.card .card {
    box-shadow: none;
}

.state-options-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 230px), 1fr));
    gap: 9px;
}

.search-field {
    position: sticky;
    top: 0;
    z-index: 100;
}

.business-hours-grid {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.day-config {
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    background-color: #ffffff;
}

.time-inputs {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    margin-left: 24px;
}

.time-label {
    margin: 0;
    font-weight: 500;
    min-width: 50px;
}

.business-hours-alert {
    background-color: #ffffff;
    border-color: #dee2e6;
    color: #212529;
}
</style>
